import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import api from '../../services/api';

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', shortDescription: '', features: '', technologies: '', order: 0, featured: false });

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try { const res = await api.get('/services?limit=100'); setServices(res.data.services); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...formData, features: formData.features.split(',').map(t => t.trim()).filter(Boolean), technologies: formData.technologies.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/services/${editing._id}`, data);
      else await api.post('/services', data);
      setShowModal(false);
      setEditing(null);
      setFormData({ title: '', description: '', shortDescription: '', features: '', technologies: '', order: 0, featured: false });
      fetchServices();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => { if (window.confirm('Delete this service?')) { await api.delete(`/services/${id}`); fetchServices(); } };

  if (loading) return <div className="animate-pulse h-96 bg-gray-200 rounded-xl"></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Services</h2>
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700"><Plus size={20} /> Add Service</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((s) => (
          <div key={s._id} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{s.title}</h3>
              {s.featured && <span className="px-2 py-1 text-xs bg-cyan-100 text-cyan-700 rounded-full">Featured</span>}
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">{s.shortDescription}</p>
            {s.technologies?.length > 0 && <div className="flex flex-wrap gap-2 mb-4">
              {s.technologies.slice(0, 4).map((t, i) => <span key={i} className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">{t}</span>)}
            </div>}
            <div className="flex gap-2 pt-4 border-t">
              <button onClick={() => { setEditing(s); setFormData({ ...s, features: s.features?.join(', '), technologies: s.technologies?.join(', ') }); setShowModal(true); }} className="flex-1 flex items-center justify-center gap-1 py-2 text-cyan-600 hover:bg-cyan-50 rounded-lg"><Edit2 size={16} /> Edit</button>
              <button onClick={() => handleDelete(s._id)} className="flex-1 flex items-center justify-center gap-1 py-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      {services.length === 0 && <div className="text-center py-12 text-gray-500">No services found</div>}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b"><h3 className="text-xl font-semibold">{editing ? 'Edit Service' : 'Add Service'}</h3><button onClick={() => setShowModal(false)}><X size={24} /></button></div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <input type="text" placeholder="Service Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" required />
              <textarea placeholder="Short Description" value={formData.shortDescription} onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" rows={2} />
              <textarea placeholder="Full Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" rows={4} required />
              <input type="text" placeholder="Features (comma-separated)" value={formData.features} onChange={(e) => setFormData({ ...formData, features: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
              <input type="text" placeholder="Technologies (comma-separated)" value={formData.technologies} onChange={(e) => setFormData({ ...formData, technologies: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
              <input type="number" placeholder="Order" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="w-4 h-4 rounded" /> Featured</label>
              <div className="flex gap-4 pt-4"><button type="submit" className="flex-1 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">{editing ? 'Update' : 'Create'}</button><button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}